import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { loadDestinationBySlug, loadFestivalsApiFirst, loadGuideBySlug } from '../../utils/cmsContentLoader';
import TouristWayfinder from '../shared/TouristWayfinder';
import { Seo, Loading, EmptyContent, ContentAdSlot } from '../shared';
import DestinationCard from './DestinationCard';
import DestinationMap from './DestinationMap';
import QuestionBlock from '../explore/QuestionBlock';
import KnowledgeFlow from '../explore/KnowledgeFlow';
import FaqAccordion from '../explore/FaqAccordion';
import DestinationBookingSection from '../booking/DestinationBookingSection';
import OfficialCitation from '../explore/OfficialCitation';
import {
	faqsForDestination, photoSpotsFor, knowledgeFlowFor, relatedGuideSlugs,
} from '../../data/destinationKnowledge';
import { OFFICIAL_SOURCES, LAST_VERIFIED } from '../../data/officialSources';
import { EXPLORE_IMAGES } from '../../data/exploreImages';
import '../../styles/destinations.scss';
import '../../styles/explore.scss';
import '../../styles/tourist-nav.scss';

const CATEGORY_LABELS = {
	national_park: 'National Park', unesco_site: 'UNESCO World Heritage Site', lake: 'Lake', waterfall: 'Waterfall',
	temple: 'Temple', monastery: 'Monastery', museum: 'Museum', viewpoint: 'Trek / Viewpoint', other: 'Destination'
};
const BUDGET_LABELS = { budget: 'Budget', mid_range: 'Mid-range', luxury: 'Luxury' };
const CURRENT_MONTH_NUM = new Date().getMonth() + 1;

const TOC = [
	{ id: 'why-visit', label: 'Why visit?' },
	{ id: 'famous', label: 'Why is it famous?' },
	{ id: 'history', label: 'History' },
	{ id: 'culture', label: 'Culture' },
	{ id: 'season', label: 'Best season' },
	{ id: 'weather', label: 'Weather' },
	{ id: 'transport', label: 'How to reach' },
	{ id: 'practical', label: 'Fees & hours' },
	{ id: 'permits', label: 'Permits' },
	{ id: 'difficulty', label: 'Difficulty' },
	{ id: 'audience', label: 'Who is it for?' },
	{ id: 'photos', label: 'Photo spots' },
	{ id: 'food', label: 'Local food' },
	{ id: 'dining', label: 'Restaurants' },
	{ id: 'safety', label: 'Safety' },
	{ id: 'mistakes', label: 'Common mistakes' },
	{ id: 'nearby', label: 'Nearby' },
	{ id: 'faqs', label: 'FAQs' },
];

function PracticalGrid({ d }) {
	const items = [
		d.entry_fee && { label: 'Entry fee', value: d.entry_fee },
		d.opening_hours && { label: 'Opening hours', value: d.opening_hours },
		d.official_contact && { label: 'Official contact', value: d.official_contact },
		d.time_required && { label: 'Time needed', value: d.time_required },
		d.altitude_m && { label: 'Altitude', value: `${d.altitude_m}m` },
		d.budget_level && { label: 'Budget level', value: BUDGET_LABELS[d.budget_level] || d.budget_level },
	].filter(Boolean);

	if (items.length === 0) return null;
	return (
		<div className="practical-grid">
			{items.map((item) => (
				<div key={item.label} className="practical-item">
					<span className="practical-item__label">{item.label}</span>
					<span className="practical-item__value">{item.value}</span>
				</div>
			))}
		</div>
	);
}

function DestinationDetailPage() {
	const { slug } = useParams();
	const [destination, setDestination] = useState(null);
	const [loading, setLoading] = useState(true);
	const [notFound, setNotFound] = useState(false);
	const [districtFestivals, setDistrictFestivals] = useState([]);
	const [relatedGuides, setRelatedGuides] = useState([]);

	useEffect(() => {
		setLoading(true);
		setNotFound(false);
		loadDestinationBySlug(slug)
			.then((data) => {
				if (data) setDestination(data);
				else setNotFound(true);
			})
			.catch(() => setNotFound(true))
			.finally(() => setLoading(false));
	}, [slug]);

	useEffect(() => {
		if (!destination || !destination.district) {
			setDistrictFestivals([]);
			return;
		}
		loadFestivalsApiFirst({ district_id: destination.district.id })
			.then(setDistrictFestivals)
			.catch(() => setDistrictFestivals([]));
	}, [destination]);

	useEffect(() => {
		if (!destination) return;
		const slugs = relatedGuideSlugs(destination);
		Promise.all(slugs.map((s) => loadGuideBySlug(s).catch(() => null)))
			.then((guides) => setRelatedGuides(guides.filter(Boolean)));
	}, [destination]);

	if (loading) return <Loading />;

	if (notFound || !destination) {
		return (
			<div className="container py-5">
				<EmptyContent>
					<i className="fas fa-map-marked-alt text-muted" style={{ fontSize: '2rem' }} aria-hidden="true" />
					<h3 className="mt-3">Destination not found</h3>
					<Link to="/#places" className="btn btn-primary mt-2">Browse all places</Link>
				</EmptyContent>
			</div>
		);
	}

	const d = destination;
	const municipality = d.municipality;
	const climate = (d.climate || []).filter((c) => c.avg_high_c != null);
	const currentClimate = climate.find((c) => c.month === CURRENT_MONTH_NUM);
	const familyTag = (d.tags || []).find((t) => t.slug === 'family-friendly');
	const seniorTag = (d.tags || []).find((t) => t.slug === 'senior-friendly');
	const soloTag = (d.tags || []).find((t) => t.slug === 'solo-travel-friendly');
	const faqs = faqsForDestination(d);
	const photoSpots = photoSpotsFor(d);
	const flow = knowledgeFlowFor(d);
	const needsParkPermit = d.category === 'national_park';
	const heroImage = d.hero_image_url || EXPLORE_IMAGES.himalaya;

	return (
		<div className="destination-detail explore-page tourist-page">
			<Seo
				title={`${d.name} — Nepal Travel Guide`}
				path={`/destinations/${d.slug}`}
				description={d.summary}
				image={d.hero_image_url}
				schema={{
					'@context': 'https://schema.org',
					'@type': 'TouristAttraction',
					name: d.name,
					description: d.summary,
					...(d.latitude && d.longitude ? { geo: { '@type': 'GeoCoordinates', latitude: d.latitude, longitude: d.longitude } } : {})
				}}
			/>

			<TouristWayfinder section="places" currentLabel={d.name} />

			<header className="explore-hero explore-hero--compact">
				<div className="explore-hero__bg" aria-hidden="true">
					<img src={heroImage} alt="" />
				</div>
				<div className="explore-hero__overlay" aria-hidden="true" />
				<div className="container explore-hero__content">
					<span className="explore-hero__eyebrow">{CATEGORY_LABELS[d.category] || d.category}</span>
					<h1 className="explore-hero__title">{d.name}</h1>
					{d.summary && <p className="explore-hero__lead">{d.summary}</p>}
				</div>
			</header>

			<div className="container destination-detail__body">
				<div className="quick-facts">
					{d.best_season && <div className="quick-fact"><i className="fas fa-calendar-alt" aria-hidden="true" /><div><span className="quick-fact__label">Best season</span><span className="quick-fact__value">{d.best_season}</span></div></div>}
					{d.difficulty && <div className="quick-fact"><i className="fas fa-hiking" aria-hidden="true" /><div><span className="quick-fact__label">Difficulty</span><span className="quick-fact__value">{d.difficulty}</span></div></div>}
					{d.budget_level && <div className="quick-fact"><i className="fas fa-wallet" aria-hidden="true" /><div><span className="quick-fact__label">Budget</span><span className="quick-fact__value">{BUDGET_LABELS[d.budget_level]}</span></div></div>}
					{d.time_required && <div className="quick-fact"><i className="fas fa-clock" aria-hidden="true" /><div><span className="quick-fact__label">Time needed</span><span className="quick-fact__value">{d.time_required}</span></div></div>}
				</div>

				<div className="knowledge-layout">
					<nav className="knowledge-toc d-none d-lg-block" aria-label="On this page">
						<p className="knowledge-toc__title">On this page</p>
						{TOC.map((t) => (
							<a key={t.id} href={`#${t.id}`} className="knowledge-toc__link">{t.label}</a>
						))}
					</nav>

					<div className="knowledge-content">
						<QuestionBlock id="why-visit" question="Why visit?" unconfirmed={!d.why_visit}>
							{d.why_visit && <p>{d.why_visit}</p>}
						</QuestionBlock>

						<QuestionBlock id="famous" question="Why is it famous?" unconfirmed={!d.body && !d.summary}>
							<p>{d.body || d.summary}</p>
						</QuestionBlock>

						<QuestionBlock id="history" question="What is the history?" unconfirmed={!d.history}>
							{d.history && <p>{d.history}</p>}
						</QuestionBlock>

						<QuestionBlock id="culture" question="What about the local culture?" unconfirmed={!d.culture}>
							{d.culture && <p>{d.culture}</p>}
						</QuestionBlock>

						{d.locals_know && (
							<div className="locals-know">
								<div className="locals-know__badge"><i className="fas fa-user-friends" aria-hidden="true" /> Things Locals Know</div>
								<p className="locals-know__text">{d.locals_know}</p>
							</div>
						)}

						<QuestionBlock id="season" question="When is the best time to visit?" unconfirmed={!d.best_season}>
							{d.best_season && <p><strong>Best:</strong> {d.best_season}</p>}
							{d.worst_season && <p><strong>Avoid:</strong> {d.worst_season}</p>}
							<p>See the <Link to="/seasonal-discovery">complete seasonal guide</Link> for month-by-month weather, festivals, and trekking conditions across Nepal.</p>
						</QuestionBlock>

						<QuestionBlock id="weather" question="What is the weather like?" unconfirmed={climate.length === 0}>
							{currentClimate && (
								<p>This month ({currentClimate.month_name}), {d.district && d.district.name} typically sees <strong>{currentClimate.avg_high_c}°C / {currentClimate.avg_low_c}°C</strong> (high/low){currentClimate.rainfall_mm ? `, with around ${currentClimate.rainfall_mm}mm rainfall` : ''}. {currentClimate.notes}</p>
							)}
							{climate.length > 0 && (
								<div className="climate-table-wrap mt-3">
									<table className="climate-table">
										<thead><tr><th>Month</th><th>High / Low</th><th>Rainfall</th><th>Notes</th></tr></thead>
										<tbody>
											{climate.map((c) => (
												<tr key={c.month}>
													<td>{c.month_name}</td>
													<td>{c.avg_high_c}°C / {c.avg_low_c}°C</td>
													<td>{c.rainfall_mm}mm</td>
													<td className="climate-table__notes">{c.notes}</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							)}
						</QuestionBlock>

						<QuestionBlock id="transport" question="How do I get there?" unconfirmed={!(municipality && (municipality.transport_notes || municipality.ride_sharing_notes))}>
							{municipality && municipality.transport_notes && <p>{municipality.transport_notes}</p>}
							{municipality && municipality.ride_sharing_notes && <p><strong>Getting around locally:</strong> {municipality.ride_sharing_notes}</p>}
							<p>See the <Link to="/transportation">Transportation Hub</Link> for flights, buses, jeeps, and ride-apps between cities.</p>
						</QuestionBlock>

						<QuestionBlock id="practical" question="What does it cost and when is it open?" unconfirmed={!d.entry_fee && !d.opening_hours && !d.official_contact}>
							<PracticalGrid d={d} />
						</QuestionBlock>

						<QuestionBlock id="permits" question="Do I need a permit?">
							{needsParkPermit ? (
								<p>Yes — national park entry permits are required. Trekking routes may additionally need TIMS cards and route-specific permits. See the <Link to="/government">Visa & Permits Hub</Link> and <Link to="/guides/tims-card-trekking-permits">TIMS & trekking permits guide</Link> for current fees and rules.</p>
							) : (
								<p>No specific permit is flagged for this site, but restricted areas across Nepal require special permits issued through registered agencies. If your itinerary includes trekking above 3,000m or entering conservation areas, check the <Link to="/government">Visa & Permits Hub</Link>.</p>
							)}
							{d.drone_allowed != null && <p><strong>Drone:</strong> {d.drone_allowed ? 'Allowed (confirm current CAAN rules)' : 'Not allowed or restricted'} — see Government Hub for filming permits.</p>}
							<OfficialCitation
								sourceName={needsParkPermit ? OFFICIAL_SOURCES.dnp.name : OFFICIAL_SOURCES.ntb.name}
								sourceUrl={needsParkPermit ? OFFICIAL_SOURCES.dnp.url : OFFICIAL_SOURCES.ntb.url}
								lastVerified={LAST_VERIFIED}
								subjectToChange
							/>
						</QuestionBlock>

						<QuestionBlock id="difficulty" question="How difficult is it?" unconfirmed={!d.difficulty}>
							{d.difficulty && <p>Rated <strong>{d.difficulty}</strong>.{d.altitude_m ? ` Altitude: ${d.altitude_m}m.` : ''}</p>}
							{d.camping_allowed != null && <p><strong>Camping:</strong> {d.camping_allowed ? 'Allowed' : 'Not allowed or not practical'}</p>}
						</QuestionBlock>

						<QuestionBlock id="audience" question="Who is this good for?">
							<div className="audience-badges">
								{familyTag && <span className="audience-badge">Family friendly</span>}
								{seniorTag && <span className="audience-badge">Senior friendly</span>}
								{soloTag && <span className="audience-badge">Solo travel friendly</span>}
								{d.accessibility_notes && <span className="audience-badge audience-badge--neutral">Accessibility info available</span>}
								{!familyTag && !seniorTag && !soloTag && <span className="audience-badge audience-badge--neutral">Check difficulty and altitude for your group</span>}
							</div>
							{d.accessibility_notes && <p><strong>Accessibility:</strong> {d.accessibility_notes}</p>}
						</QuestionBlock>

						<QuestionBlock id="photos" question="Where are the best photo spots?">
							<ul>
								{photoSpots.map((spot) => <li key={spot}>{spot}</li>)}
							</ul>
							<p>Photography tips by season are in the <Link to="/seasonal-discovery">Seasonal Discovery hub</Link>.</p>
						</QuestionBlock>

						<QuestionBlock id="food" question="What local food should I try?" unconfirmed={!d.local_food}>
							{d.local_food && <p>{d.local_food}</p>}
						</QuestionBlock>

						<QuestionBlock id="dining" question="Where can I eat nearby?" unconfirmed={!d.dining_notes}>
							{d.dining_notes ? <p>{d.dining_notes}</p> : (
								<p>Restaurant listings for this area are being expanded. In the meantime, ask your hotel or guide for current recommendations — local knowledge beats outdated guidebook listings.</p>
							)}
						</QuestionBlock>

						<QuestionBlock id="safety" question="Is it safe? What about emergencies?">
							{d.emergency_info && <p>{d.emergency_info}</p>}
							{municipality && municipality.emergency_services_notes && <p><strong>Local services:</strong> {municipality.emergency_services_notes}</p>}
							{municipality && municipality.atm_notes && <p><strong>ATMs:</strong> {municipality.atm_notes}</p>}
							{municipality && municipality.internet_notes && <p><strong>Internet:</strong> {municipality.internet_notes}</p>}
							<p>Emergency numbers: Police <strong>100</strong>, Ambulance <strong>102</strong>. See <Link to="/living-in-nepal">Living in Nepal</Link> for safety and scam awareness.</p>
						</QuestionBlock>

						{d.tourist_mistakes && (
							<div className="tourist-mistakes" id="mistakes">
								<div className="tourist-mistakes__badge"><i className="fas fa-exclamation-triangle" aria-hidden="true" /> Common Tourist Mistakes</div>
								<p className="tourist-mistakes__text">{d.tourist_mistakes}</p>
							</div>
						)}

						{d.latitude && d.longitude && (
							<section className="destination-section" id="nearby">
								<h2>What is nearby?</h2>
								<div className="destination-map-layout">
									<DestinationMap center={{ lat: d.latitude, lng: d.longitude, name: d.name }} nearby={d.nearby || []} />
									<div className="destination-map-panel">
										{districtFestivals.length > 0 && (
											<div className="destination-map-panel__group">
												<h4>Festivals in this area</h4>
												<ul>{districtFestivals.map((f) => <li key={f.slug}><Link to={`/festivals/${f.slug}`}>{f.name}</Link></li>)}</ul>
											</div>
										)}
										<div className="destination-map-panel__group">
											<h4>Shopping nearby</h4>
											<p className="mb-0">See the <Link to="/shopping">Shopping in Nepal guide</Link> for markets and souvenirs in this region.</p>
										</div>
									</div>
								</div>
								{d.nearby && d.nearby.length > 0 && (
									<div className="destinations-grid mt-4">
										{d.nearby.map((n) => <DestinationCard key={n.slug} destination={n} />)}
									</div>
								)}
							</section>
						)}

						{relatedGuides.length > 0 && (
							<section className="destination-section">
								<h2>Related guides</h2>
								<div className="related-rail">
									{relatedGuides.map((g) => (
										<Link key={g.slug} to={`/guides/${g.slug}`} className="related-link">{g.title}</Link>
									))}
								</div>
							</section>
						)}

						<section id="faqs">
							<h2 style={{ fontSize: '1.35rem', color: '#253f72', marginBottom: '1rem' }}>Frequently asked questions</h2>
							<FaqAccordion items={faqs} />
						</section>

						<ContentAdSlot layout="destination" className="adsense-unit--in-content my-4" />

						<KnowledgeFlow steps={flow} />

						<DestinationBookingSection destination={d} />

						<div className="explore-cta-band">
							<h3>Ready to go?</h3>
							<p>Book a private tour customized to your dates, or browse group trips with fixed departures.</p>
							<div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
								<Link to="/packages" className="explore-hero__cta explore-hero__cta--primary">Book a private tour</Link>
								<Link to="/group-trips" className="explore-hero__cta explore-hero__cta--ghost">Browse group trips</Link>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export default DestinationDetailPage;
